import {
  ActionKind,
  actionHash,
  canonicalCalls,
  createSubwalletPayload,
  initialNoteHash,
  reserveTransferPayload,
  shieldPayload,
  subwalletCallPayload,
  type V2Call,
  type V2Coin,
} from "./action";
import { base64Encode } from "./encoding";
import { addressHash, controlNullifier, reserveNullifier } from "./field";
import type { V2KeyContext, V2Keys } from "./keys";
import {
  createControlNote,
  createReserveNote,
  encryptNote,
  type ControlNote,
  type ReserveNote,
  type V2Note,
} from "./notes";
import {
  generateV2Proof,
  type CircuitName,
  type Groth16Proof,
  type ProvingManifest,
} from "./proofs";
import type { RecoveredNote } from "./recovery";
import { authorizedFeePayload, type ActionFeePolicy } from "./feePolicy";

export type ProofGenerator = (
  circuit: CircuitName,
  input: Record<string, unknown>
) => Promise<Groth16Proof>;

export const manifestProofGenerator =
  (manifest: ProvingManifest): ProofGenerator =>
  (circuit, input) =>
    generateV2Proof({ circuit, input, manifest });

export type PreparedV2Execution = {
  msg: Record<string, unknown>;
  funds: V2Coin[];
  outputs: V2Note[];
  consumedNullifiers: string[];
  /**
   * Funding bundle: these proxy messages (transfer_to_subwallet, then the
   * sub's call) go out in one atomic transaction. `msg` is the last one.
   */
  bundle?: Record<string, unknown>[];
};

type OperationContext = {
  keys: V2Keys;
  context: V2KeyContext;
  prove: ProofGenerator;
};

type ActionContext = OperationContext & {
  relayerAddress: string;
  expiryHeight: bigint;
  feePolicy?: ActionFeePolicy;
};

const decimalField = (value: string): string => BigInt(`0x${value}`).toString();
const decimal = (value: bigint): string => value.toString();

const reserveInput = (value: RecoveredNote, keys: V2Keys): ReserveNote => {
  if (
    value.spent ||
    value.note.kind !== "reserve" ||
    value.note.ownerKey !== keys.ownerKey
  ) {
    throw new Error(
      "Reserve note is not an active note owned by this privacy session"
    );
  }
  return value.note;
};

const controlInput = (value: RecoveredNote, keys: V2Keys): ControlNote => {
  if (
    value.spent ||
    value.note.kind !== "control" ||
    value.note.ownerKey !== keys.ownerKey
  ) {
    throw new Error(
      "Control note is not an active note owned by this privacy session"
    );
  }
  return value.note;
};

const proofHex = (proof: Groth16Proof): string => proof.proofHex;

export const prepareReserveDeposit = async (
  operation: OperationContext,
  value: V2Coin
): Promise<PreparedV2Execution> => {
  const amount = BigInt(value.amount);
  const note = await createReserveNote(operation.keys, value.denom, amount);
  const encrypted = await encryptNote(operation.keys, operation.context, note);
  const proof = await operation.prove("reserve_deposit", {
    commitment: decimalField(note.commitment),
    contextHash: decimalField(
      await initialNoteHash({
        chainId: operation.context.chainId,
        proxyContract: operation.context.proxyAddress,
        kind: "reserve",
        commitment: note.commitment,
        ciphertext: encrypted,
      })
    ),
    amount: decimal(amount),
    denomHash: decimalField(note.denomHash),
    ownerKey: decimalField(note.ownerKey),
    rho: decimalField(note.rho),
    blinding: decimalField(note.blinding),
  });
  return {
    msg: {
      deposit: {
        commitment: note.commitment,
        encrypted_note: base64Encode(encrypted),
        proof: proofHex(proof),
      },
    },
    funds: [value],
    outputs: [note],
    consumedNullifiers: [],
  };
};

export const prepareControlRegistration = async (
  operation: OperationContext,
  registrationFee: V2Coin
): Promise<PreparedV2Execution> => {
  const note = await createControlNote(operation.keys, {
    subwallet: null,
    controlNonce: 0n,
  });
  const encrypted = await encryptNote(operation.keys, operation.context, note);
  const proof = await operation.prove("control_register", {
    commitment: decimalField(note.commitment),
    contextHash: decimalField(
      await initialNoteHash({
        chainId: operation.context.chainId,
        proxyContract: operation.context.proxyAddress,
        kind: "control",
        commitment: note.commitment,
        ciphertext: encrypted,
      })
    ),
    ownerKey: decimalField(note.ownerKey),
    rho: decimalField(note.rho),
    blinding: decimalField(note.blinding),
  });
  return {
    msg: {
      register_control: {
        commitment: note.commitment,
        encrypted_note: base64Encode(encrypted),
        proof: proofHex(proof),
      },
    },
    funds: [registrationFee],
    outputs: [note],
    consumedNullifiers: [],
  };
};

const prepareReserveTransfer = async (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    destination: string;
    spendAmount: bigint;
    relayerFee: bigint;
    kind: ActionKind.ReserveWithdraw | ActionKind.ReserveToSubwallet;
  }
): Promise<PreparedV2Execution> => {
  const input = reserveInput(params.input, operation.keys);
  const inputAmount = BigInt(input.amount);
  if (params.spendAmount <= 0n || params.relayerFee < 0n) {
    throw new Error("Invalid private reserve transfer amount");
  }
  const change = inputAmount - params.spendAmount - params.relayerFee;
  if (change < 0n)
    throw new Error("Private reserve note balance is insufficient");
  const output = await createReserveNote(operation.keys, input.denom, change, {
    allowZero: true,
  });
  const encrypted = await encryptNote(
    operation.keys,
    operation.context,
    output
  );
  const nullifier = await reserveNullifier(input.ownerKey, input.rho);
  const action = await actionHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    actionKind: params.kind,
    canonicalPayload: authorizedFeePayload(
      reserveTransferPayload({
        denom: input.denom,
        destination: params.destination,
        spendAmount: params.spendAmount,
      }),
      operation.feePolicy
    ),
    outputCommitments: [output.commitment],
    encryptedOutputPayloads: [encrypted],
    relayerAddress: operation.relayerAddress,
    relayerFee: params.relayerFee,
    expiryHeight: operation.expiryHeight,
  });
  const proof = await operation.prove("reserve_spend", {
    root: decimalField(params.root),
    nullifier: decimalField(nullifier),
    actionHash: decimalField(action),
    outputCommitment: decimalField(output.commitment),
    spendAmount: decimal(params.spendAmount),
    relayerFee: decimal(params.relayerFee),
    denomHash: decimalField(input.denomHash),
    ownerKey: decimalField(input.ownerKey),
    rho: decimalField(input.rho),
    noteAmount: input.amount,
    blinding: decimalField(input.blinding),
    outputRho: decimalField(output.rho),
    outputBlinding: decimalField(output.blinding),
    pathElements: params.input.pathElements.map(decimalField),
    pathIndices: params.input.pathIndices,
  });
  const body = {
    root: params.root,
    nullifier,
    output_commitment: output.commitment,
    encrypted_output: base64Encode(encrypted),
    denom: input.denom,
    spend_amount: params.spendAmount.toString(),
    relayer_fee: params.relayerFee.toString(),
    ...(operation.feePolicy && {
      fee_authorization: operation.feePolicy.authorization,
    }),
    expiry_height: Number(operation.expiryHeight),
    proof: proofHex(proof),
  };
  return {
    msg:
      params.kind === ActionKind.ReserveWithdraw
        ? { withdraw: { ...body, recipient: params.destination } }
        : { transfer_to_subwallet: { ...body, subwallet: params.destination } },
    funds: [],
    outputs: [output],
    consumedNullifiers: [nullifier],
  };
};

export const prepareReserveWithdraw = (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    recipient: string;
    spendAmount: bigint;
    relayerFee?: bigint;
  }
): Promise<PreparedV2Execution> =>
  prepareReserveTransfer(operation, {
    ...params,
    destination: params.recipient,
    relayerFee: params.relayerFee ?? 0n,
    kind: ActionKind.ReserveWithdraw,
  });

export const prepareReserveToSubwallet = (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    subwallet: string;
    spendAmount: bigint;
    relayerFee?: bigint;
  }
): Promise<PreparedV2Execution> =>
  prepareReserveTransfer(operation, {
    ...params,
    destination: params.subwallet,
    relayerFee: params.relayerFee ?? 0n,
    kind: ActionKind.ReserveToSubwallet,
  });

export const prepareReserveMerge = async (
  operation: ActionContext,
  params: { root: string; first: RecoveredNote; second: RecoveredNote }
): Promise<PreparedV2Execution> => {
  if (params.first.index === params.second.index)
    throw new Error("Cannot merge a note with itself");
  const first = reserveInput(params.first, operation.keys);
  const second = reserveInput(params.second, operation.keys);
  if (first.denom !== second.denom || first.denomHash !== second.denomHash) {
    throw new Error(
      "Only reserve notes of the same denomination can be merged"
    );
  }
  const output = await createReserveNote(
    operation.keys,
    first.denom,
    BigInt(first.amount) + BigInt(second.amount)
  );
  const encrypted = await encryptNote(
    operation.keys,
    operation.context,
    output
  );
  const [nullifierA, nullifierB] = await Promise.all([
    reserveNullifier(first.ownerKey, first.rho),
    reserveNullifier(second.ownerKey, second.rho),
  ]);
  const action = await actionHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    actionKind: ActionKind.ReserveMerge,
    canonicalPayload: new Uint8Array(),
    outputCommitments: [output.commitment],
    encryptedOutputPayloads: [encrypted],
    relayerAddress: operation.relayerAddress,
    relayerFee: 0n,
    expiryHeight: operation.expiryHeight,
  });
  const proof = await operation.prove("reserve_merge", {
    root: decimalField(params.root),
    nullifierA: decimalField(nullifierA),
    nullifierB: decimalField(nullifierB),
    actionHash: decimalField(action),
    outputCommitment: decimalField(output.commitment),
    denomHash: decimalField(first.denomHash),
    ownerKey: decimalField(first.ownerKey),
    rhoA: decimalField(first.rho),
    amountA: first.amount,
    blindingA: decimalField(first.blinding),
    pathElementsA: params.first.pathElements.map(decimalField),
    pathIndicesA: params.first.pathIndices,
    rhoB: decimalField(second.rho),
    amountB: second.amount,
    blindingB: decimalField(second.blinding),
    pathElementsB: params.second.pathElements.map(decimalField),
    pathIndicesB: params.second.pathIndices,
    outputRho: decimalField(output.rho),
    outputBlinding: decimalField(output.blinding),
  });
  return {
    msg: {
      merge_reserve_notes: {
        root: params.root,
        nullifier_a: nullifierA,
        nullifier_b: nullifierB,
        output_commitment: output.commitment,
        encrypted_output: base64Encode(encrypted),
        expiry_height: Number(operation.expiryHeight),
        proof: proofHex(proof),
      },
    },
    funds: [],
    outputs: [output],
    consumedNullifiers: [nullifierA, nullifierB],
  };
};

export const prepareCreateSubwallet = async (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    salt: Uint8Array;
    predictedSubwallet: string;
  }
): Promise<PreparedV2Execution> => {
  const input = controlInput(params.input, operation.keys);
  if (input.subwallet !== null || input.controlNonce !== "0") {
    throw new Error(
      "A subwallet can only be created from an unassigned control note"
    );
  }
  if (params.salt.length < 1 || params.salt.length > 64)
    throw new Error("Invalid subwallet salt");
  const output = await createControlNote(operation.keys, {
    subwallet: params.predictedSubwallet,
    controlNonce: 0n,
  });
  const encrypted = await encryptNote(
    operation.keys,
    operation.context,
    output
  );
  const nullifier = await controlNullifier(input.ownerKey, input.rho);
  const subwalletHash = await addressHash(params.predictedSubwallet);
  const action = await actionHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    actionKind: ActionKind.CreateSubwallet,
    canonicalPayload: createSubwalletPayload(
      params.salt,
      params.predictedSubwallet
    ),
    outputCommitments: [output.commitment],
    encryptedOutputPayloads: [encrypted],
    relayerAddress: operation.relayerAddress,
    relayerFee: 0n,
    expiryHeight: operation.expiryHeight,
  });
  const proof = await operation.prove("create_subwallet", {
    root: decimalField(params.root),
    nullifier: decimalField(nullifier),
    actionHash: decimalField(action),
    outputCommitment: decimalField(output.commitment),
    subwalletHash: decimalField(subwalletHash),
    ownerKey: decimalField(input.ownerKey),
    rho: decimalField(input.rho),
    blinding: decimalField(input.blinding),
    outputRho: decimalField(output.rho),
    outputBlinding: decimalField(output.blinding),
    pathElements: params.input.pathElements.map(decimalField),
    pathIndices: params.input.pathIndices,
  });
  return {
    msg: {
      create_subwallet: {
        root: params.root,
        nullifier,
        output_commitment: output.commitment,
        encrypted_output: base64Encode(encrypted),
        salt: base64Encode(params.salt),
        expiry_height: Number(operation.expiryHeight),
        proof: proofHex(proof),
      },
    },
    funds: [],
    outputs: [output],
    consumedNullifiers: [nullifier],
  };
};

export const prepareSubwalletCall = async (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    subwallet: string;
    calls: V2Call[];
    relayerFee?: V2Coin;
  }
): Promise<PreparedV2Execution> => {
  const input = controlInput(params.input, operation.keys);
  if (input.subwallet !== params.subwallet) {
    throw new Error("Control note does not belong to the requested subwallet");
  }
  const output = await createControlNote(operation.keys, {
    subwallet: params.subwallet,
    controlNonce: BigInt(input.controlNonce) + 1n,
  });
  const encrypted = await encryptNote(
    operation.keys,
    operation.context,
    output
  );
  const nullifier = await controlNullifier(input.ownerKey, input.rho);
  const subwalletHash = await addressHash(params.subwallet);
  canonicalCalls(params.calls);
  const relayerFee = params.relayerFee ? BigInt(params.relayerFee.amount) : 0n;
  const action = await actionHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    actionKind:
      params.calls.length === 1
        ? ActionKind.SubwalletCall
        : ActionKind.SubwalletBatch,
    canonicalPayload: authorizedFeePayload(
      subwalletCallPayload({
        subwallet: params.subwallet,
        calls: params.calls,
        relayerFee: params.relayerFee,
      }),
      operation.feePolicy
    ),
    outputCommitments: [output.commitment],
    encryptedOutputPayloads: [encrypted],
    relayerAddress: operation.relayerAddress,
    relayerFee,
    expiryHeight: operation.expiryHeight,
  });
  const proof = await operation.prove("control_action", {
    root: decimalField(params.root),
    nullifier: decimalField(nullifier),
    actionHash: decimalField(action),
    outputCommitment: decimalField(output.commitment),
    subwalletHash: decimalField(subwalletHash),
    ownerKey: decimalField(input.ownerKey),
    rho: decimalField(input.rho),
    controlNonce: input.controlNonce,
    blinding: decimalField(input.blinding),
    outputRho: decimalField(output.rho),
    outputBlinding: decimalField(output.blinding),
    pathElements: params.input.pathElements.map(decimalField),
    pathIndices: params.input.pathIndices,
  });
  return {
    msg: {
      call_subwallet: {
        root: params.root,
        nullifier,
        output_commitment: output.commitment,
        encrypted_output: base64Encode(encrypted),
        subwallet: params.subwallet,
        calls: params.calls,
        // Keep the wire shape exact for sponsored calls. JSON.stringify drops
        // undefined keys, while the contract/relayer ABI requires explicit null.
        relayer_fee: params.relayerFee ?? null,
        ...(operation.feePolicy && {
          fee_authorization: operation.feePolicy.authorization,
        }),
        expiry_height: Number(operation.expiryHeight),
        proof: proofHex(proof),
      },
    },
    funds: [],
    outputs: [output],
    consumedNullifiers: [nullifier],
  };
};

export const prepareShieldFromSubwallet = async (
  operation: ActionContext,
  params: {
    root: string;
    input: RecoveredNote;
    subwallet: string;
    amount: V2Coin;
  }
): Promise<PreparedV2Execution> => {
  const input = controlInput(params.input, operation.keys);
  if (input.subwallet !== params.subwallet) {
    throw new Error("Control note does not belong to the requested subwallet");
  }
  const amount = BigInt(params.amount.amount);
  const [controlOutput, reserveOutput] = await Promise.all([
    createControlNote(operation.keys, {
      subwallet: params.subwallet,
      controlNonce: BigInt(input.controlNonce) + 1n,
    }),
    createReserveNote(operation.keys, params.amount.denom, amount),
  ]);
  const [encryptedControl, encryptedReserve] = await Promise.all([
    encryptNote(operation.keys, operation.context, controlOutput),
    encryptNote(operation.keys, operation.context, reserveOutput),
  ]);
  const nullifier = await controlNullifier(input.ownerKey, input.rho);
  const subwalletHash = await addressHash(params.subwallet);
  const action = await actionHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    actionKind: ActionKind.SubwalletToReserve,
    canonicalPayload: shieldPayload({
      subwallet: params.subwallet,
      amount: params.amount,
      reserveCommitment: reserveOutput.commitment,
    }),
    outputCommitments: [controlOutput.commitment, reserveOutput.commitment],
    encryptedOutputPayloads: [encryptedControl, encryptedReserve],
    relayerAddress: operation.relayerAddress,
    relayerFee: 0n,
    expiryHeight: operation.expiryHeight,
  });
  const reserveContextHash = await initialNoteHash({
    chainId: operation.context.chainId,
    proxyContract: operation.context.proxyAddress,
    kind: "reserve",
    commitment: reserveOutput.commitment,
    ciphertext: encryptedReserve,
  });
  const [reserveProof, controlProof] = await Promise.all([
    operation.prove("reserve_deposit", {
      commitment: decimalField(reserveOutput.commitment),
      contextHash: decimalField(reserveContextHash),
      amount: decimal(amount),
      denomHash: decimalField(reserveOutput.denomHash),
      ownerKey: decimalField(reserveOutput.ownerKey),
      rho: decimalField(reserveOutput.rho),
      blinding: decimalField(reserveOutput.blinding),
    }),
    operation.prove("control_action", {
      root: decimalField(params.root),
      nullifier: decimalField(nullifier),
      actionHash: decimalField(action),
      outputCommitment: decimalField(controlOutput.commitment),
      subwalletHash: decimalField(subwalletHash),
      ownerKey: decimalField(input.ownerKey),
      rho: decimalField(input.rho),
      controlNonce: input.controlNonce,
      blinding: decimalField(input.blinding),
      outputRho: decimalField(controlOutput.rho),
      outputBlinding: decimalField(controlOutput.blinding),
      pathElements: params.input.pathElements.map(decimalField),
      pathIndices: params.input.pathIndices,
    }),
  ]);
  return {
    msg: {
      shield_from_subwallet: {
        root: params.root,
        nullifier,
        output_commitment: controlOutput.commitment,
        encrypted_output: base64Encode(encryptedControl),
        subwallet: params.subwallet,
        amount: params.amount,
        reserve_commitment: reserveOutput.commitment,
        encrypted_reserve_note: base64Encode(encryptedReserve),
        reserve_deposit_proof: proofHex(reserveProof),
        expiry_height: Number(operation.expiryHeight),
        control_proof: proofHex(controlProof),
      },
    },
    funds: [],
    outputs: [controlOutput, reserveOutput],
    consumedNullifiers: [nullifier],
  };
};
